/**
 * 設定 → 言葉の言い換え（縦串8、027、docs/22。射場さん 9/17 の決定：「得意先」は「顧客」に、会社ごとに切り替えられる）
 *   1. 業種の既定：工務店・職人向け（工事名・工事場所・工期・工事概要）／一般の会社向け（件名・納品先・納期・概要）
 *   2. 言い換え：言葉をそれぞれ自分の言葉に（空なら既定）。よく使う言葉を押すと入る
 *   3. 保存すると、メニュー・一覧・紙の見出し・文の中が変わる（出したあとの PDF は変わらない）
 * #156（docs/65 §7・★2 a、Figma「設定の残り（10/9）」㉔㉕）で iOS 風に。題は「言葉の言い換え」（設定のメニューの名前は今のまま）。DB・API は変えていない
 *   ・節「業種に合わせた言葉」：選ぶ行（GroupCheckRow）で2行（補足に4つの言葉）。営業力は今どおり一般だけ（工務店向けを使っている会社は2行）。
 *     押した瞬間に PUT /company/vocabulary（preset だけ変えて、言い換えはそのまま）
 *   ・節「言い換え」：全部の言葉（VOCAB_KEYS）を「何の言葉か › いまの言葉」の行。押すと1語の画面（/settings/words/:key、SettingsWordPage）。
 *     今の大きな表と1つの「保存」はやめた
 * #222（docs/22 §6）：節「明細の列」＝明細表の見出し 名称・規格・備考 の3行（業種の節と言い換えの節のあいだ）。欄は単位と同じ形（候補のプルダウン＋自由入力。
 *   components/UnitInput.tsx に候補を渡す）。1〜6文字・空は不可・3つが同じ言葉にならない（contract の lineLabelsErrors＝API と同じ）。
 *   自動で保存（欄を離れたとき・候補を選んだとき）、様子は題の右。言い換えの節（WORD_KEYS）には出さない
 */
import { LINE_LABEL_KEYS, LINE_LABEL_MAX, ONGOING_WORD_MAX, REST_MSG, SETTINGS_MSG, VOCAB_KEY_INFO, VOCAB_MSG, VOCAB_PRESET_LABEL, VocabularyPreset, WORD_KEYS, lineLabelsErrors, lineLabelsOf, type LineLabels, type VocabKey, type Vocabulary, type VocabularyUpdate } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router";
import { api, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { useBackNav, useLeaveGuard, usePageName } from "../components/BackButton.jsx";
import { GroupCheckRow, GroupInputRow, GroupList, GroupNavRow, GroupSection, GroupTextRow } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { UnitInput } from "../components/UnitInput.jsx";
import { ErrorText, Help, guardComposingEnter, useToast } from "../components/ui.jsx";
import { t } from "../labels.js";
import { uses } from "../core/uses.js";
import { AutoSaveStatus, useAutoSave } from "../lib/autoSave.jsx";

const VOCAB_QKEY = ["vocabulary"] as const;
const LIST = "/settings/words";
/** 業種の行の補足に出す4つの言葉（Figma ㉔） */
const PRESET_SUB_KEYS: readonly VocabKey[] = ["subject", "site", "period", "outline"];
/** 業種の名前（「工務店・職人向け（工事名・…）」の括弧は行の補足に出すので外す） */
const presetName = (p: VocabularyPreset) => VOCAB_PRESET_LABEL[p].replace(/（.*）$/, "");

/** 保存したあと：言葉を使う画面を取り直す */
function afterSave(qc: QueryClient, v: Vocabulary) {
  qc.setQueryData(VOCAB_QKEY, v);
  void qc.invalidateQueries({ queryKey: ME_KEY }); // メニューの「顧客」と決まり文
  void qc.invalidateQueries({ queryKey: ["company"] }); // 紙の見出し
  qc.removeQueries({ queryKey: ["estimate"] });
  qc.removeQueries({ queryKey: ["invoice"] });
}

export function SettingsWordsPage() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: VOCAB_QKEY, queryFn: () => api<Vocabulary>("GET", "/company/vocabulary") });
  /** 言い換えの候補と業種の組は製品ごと（#142 段6 No.15・19）：営業力は「一般の会社向け」だけ（工務店向けを選んでいた会社は両方） */
  const me = useMe().data;
  const product = me?.tenant.product ?? "genba";
  const dnOn = !!me?.tenant.modules.includes("sales") && uses(me, "deliveryNote");
  // {acceptance}（1b）も使う機能「{acceptance}」がオンのときだけ
  const acOn = !!me?.tenant.modules.includes("sales") && uses(me, "acceptance");
  // {ongoingProject}（#289）は使う機能「{ongoingProject}」がオンのときだけ
  const ogOn = uses(me, "ongoingProjects");
  // 明細の列（#222）：3つの言葉を1つの形で自動保存（送る直前の言い換えに3つを重ねて PUT。ほかの語は変えない）
  const line = useAutoSave<LineLabels>({
    server: q.data ? lineLabelsOf(q.data.labels) : null,
    send: async (v) => {
      const now = qc.getQueryData<Vocabulary>(VOCAB_QKEY) ?? q.data!;
      const overrides = { ...now.overrides };
      for (const k of LINE_LABEL_KEYS) overrides[k] = v[k].trim();
      const r = await api<Vocabulary>("PUT", "/company/vocabulary", { preset: now.preset, overrides } satisfies VocabularyUpdate);
      afterSave(qc, r);
      return lineLabelsOf(r.labels);
    },
    check: (v) => {
      const errors = lineLabelsErrors(v);
      return LINE_LABEL_KEYS.some((k) => errors[k]) ? { status: SETTINGS_MSG.autoSaveFailed, errors: errors as Record<string, string> } : null;
    },
  });
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 設定。送れていない明細の列の直しがあれば確かめる
  const back = useBackNav({ to: "/settings", label: "設定" }, line.unsent);
  const guard = useLeaveGuard(line.unsent);
  usePageName(REST_MSG.wordsTitle, "言い換え"); // 戻るボタンは「を」「の」なし（#240）
  const cls = useRestPageClass("wordspage");

  const save = useMutation({
    mutationFn: (v: VocabularyUpdate) => api<Vocabulary>("PUT", "/company/vocabulary", v),
    onMutate: (v) => {
      const prev = qc.getQueryData<Vocabulary>(VOCAB_QKEY);
      if (prev) qc.setQueryData(VOCAB_QKEY, { ...prev, preset: v.preset });
      return { prev };
    },
    onSuccess: (v) => {
      afterSave(qc, v);
      toast(REST_MSG.presetSaved.replace("{name}", presetName(v.preset)));
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(VOCAB_QKEY, ctx.prev);
      toast(messageOf(e), undefined, "error");
    },
  });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const data = q.data;
  const presets = data.presets;
  const base = presets[data.preset];
  const wordOf = (k: VocabKey) => (data.overrides[k] ?? "").trim() || base[k];

  const lf = line.form ?? lineLabelsOf(data.labels);
  const lineError = (k: (typeof LINE_LABEL_KEYS)[number]): string | undefined => line.errors[k] ?? (line.serverError && k === "lineName" ? line.serverError : undefined);

  return (
    <div className={cls} data-testid="words">
      <SettingsHead back={back} title={REST_MSG.wordsTitle} end={<AutoSaveStatus state={line.state} testId="line-labels-autosave" />} />
      {guard.dialog}
      <GroupSection
        title={REST_MSG.wordsSectionPreset}
        help={<Help testId="help-words-preset" text="メニュー・一覧・見積書や請求書の見出し・案内の文に使う言葉です。まず業種を選び、変えたい言葉があれば下で言い換えます。" />}
        data-testid="words-sec-preset"
      >
        <GroupList plain>
          {VocabularyPreset.options
            .filter((p) => product !== "eigyo" || p === "general" || data.preset === "construction")
            .map((p) => (
              <GroupCheckRow
                key={p}
                title={presetName(p)}
                sub={PRESET_SUB_KEYS.map((k) => presets[p][k]).join("・")}
                checked={data.preset === p}
                disabled={save.isPending}
                onChange={() => data.preset !== p && save.mutate({ preset: p, overrides: data.overrides })}
                buttonTestId={`words-preset-${p}`}
                data-preset={p}
              />
            ))}
        </GroupList>
      </GroupSection>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void line.flush();
        }}
        {...line.formProps}
      >
        <GroupSection
          title={VOCAB_MSG.lineSection}
          help={<Help testId="help-line-labels" label={`${VOCAB_MSG.lineSection}の説明`} text={VOCAB_MSG.lineHelp} />}
          data-testid="words-sec-lines"
        >
          <GroupList plain id="lineLabelList">
            {LINE_LABEL_KEYS.map((k) => {
              const err = lineError(k);
              return (
                <GroupInputRow key={k} id={`vl_${k}`} label={VOCAB_KEY_INFO[k].title} wide error={err} testId={`line-label-row-${k}`} data-word={k}>
                  <UnitInput
                    id={`vl_${k}`}
                    className="giinp"
                    label={VOCAB_KEY_INFO[k].title}
                    placeholder=" "
                    maxLength={LINE_LABEL_MAX}
                    options={VOCAB_KEY_INFO[k].suggestions[product]}
                    value={lf[k]}
                    onChange={(v) => line.set({ ...lf, [k]: v })}
                    onKeyDown={guardComposingEnter}
                    testId={`line-label-${k}`}
                  />
                </GroupInputRow>
              );
            })}
          </GroupList>
        </GroupSection>
      </form>
      <GroupSection
        title={REST_MSG.wordsSectionWords}
        help={<Help testId="help-words" text="空なら業種の既定のままです。行を押すと、言葉を変えられます（20文字まで）。" />}
        note={REST_MSG.wordsNote}
        data-testid="words-sec-words"
      >
        <GroupList plain id="wordList">
          {/* {deliveryNote}（#266）は sales の会社で使う機能「{deliveryNote}」がオンのときだけ（使わない会社の一覧に出さない＝値は残る） */}
          {WORD_KEYS.filter((k) => (k !== "deliveryNote" || dnOn) && (k !== "acceptance" || acOn) && (k !== "ongoingProject" || ogOn)).map((k) => (
            <GroupNavRow key={k} title={t(VOCAB_KEY_INFO[k].title)} value={wordOf(k)} valueTestId={`word-value-${k}`} to={`${LIST}/${k}`} buttonTestId={`word-row-${k}`} data-word={k} />
          ))}
        </GroupList>
      </GroupSection>
      {node}
    </div>
  );
}

/**
 * 設定 → 言葉の言い換え → 1語の画面（#156、docs/65 §7、Figma ㉕。/settings/words/:key）。
 *   ・上の帯「‹ 言葉の言い換え｜自動で保存されます」（S-00008 の続き、10/10 射場さん：設定の画面は自動で保存に揃える。前は「保存」→ 一覧に戻る）、大きな題＝何の言葉か。PC の2列でも左の印は「言葉（項目名）の言い換え」
 *   ・入力の行「言葉」（20文字・id は今の vw_${key}）と注「空なら既定（…）。20文字まで」
 *   ・節「よく使う言葉」＝選ぶ行（今の候補のボタン。押すと欄に入る、既定の言葉を押すと空）／節「出るところ」＝今の where の文
 *   ・自動で保存（lib/autoSave.tsx）：欄は離れたとき・Enter、よく使う言葉は押した瞬間に PUT /company/vocabulary（その語だけ変えて送る）。画面に残って題の右に「保存しました」。
 *     21文字以上は送らず欄の下に朱の1行。送れていない直しのまま「‹」・左の列で移ろうとしたら確かめる
 */
export function SettingsWordPage() {
  const { key = "" } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: VOCAB_QKEY, queryFn: () => api<Vocabulary>("GET", "/company/vocabulary") });
  const product = useMe().data?.tenant.product ?? "genba";
  // 明細の列（#222）は一覧の節「明細の列」で変える＝この1語の画面には出さない
  const k = (WORD_KEYS as readonly string[]).includes(key) ? (key as VocabKey) : null;
  const a = useAutoSave<string>({
    server: k && q.data ? (q.data.overrides[k] ?? "") : null,
    send: async (v) => {
      // 送る直前の言い換え全部（ほかの語は変えない）
      const now = qc.getQueryData<Vocabulary>(VOCAB_QKEY) ?? q.data!;
      const r = await api<Vocabulary>("PUT", "/company/vocabulary", { preset: now.preset, overrides: { ...now.overrides, [k!]: v.trim() } } satisfies VocabularyUpdate);
      afterSave(qc, r);
      return r.overrides[k!] ?? "";
    },
    // {ongoingProject}（#289）は札・タブに出るので 6文字まで（API も同じ＝VOCAB_MSG.lineTooLong）
    check: (v) => ([...v.trim()].length > maxOf(k) ? { status: SETTINGS_MSG.autoSaveFailed, errors: { word: k === "ongoingProject" ? VOCAB_MSG.lineTooLong : VOCAB_MSG.tooLong } } : null),
  });
  const cur = a.form ?? "";
  const error = a.errors.word ?? (a.serverError ? a.serverError : Object.values(a.errors)[0]);
  const back = useBackNav({ to: LIST, label: "言い換え" }, a.unsent); // 戻るボタンは「を」「の」なし（#240）
  const guard = useLeaveGuard(a.unsent);
  const info = k ? VOCAB_KEY_INFO[k] : null;
  usePageName(info ? t(info.title) : null);
  const cls = useRestPageClass("wordpage");

  if (!k || !info) return <Navigate to={LIST} replace />;
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const data = q.data;
  const base = data.presets[data.preset][k];
  const word = cur.trim() || base;
  const saveBtn = <AutoSaveStatus state={a.state} testId="word-autosave" />;

  return (
    <div className={cls} data-testid="word">
      <SettingsHead back={back} title={t(info.title)} end={saveBtn} titleTestId="word-title" />
      {guard.dialog}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void a.flush();
        }}
        {...a.formProps}
      >
        <GroupSection note={REST_MSG.wordNote.replace("{base}", base).replace("{max}", String(maxOf(k)))} data-testid="word-sec-input">
          <GroupList plain>
            <GroupInputRow id={`vw_${k}`} label={REST_MSG.word} wide error={error}>
              <input
                id={`vw_${k}`}
                className="giinp"
                maxLength={maxOf(k)}
                placeholder={base}
                value={cur}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `vw_${k}-err` : undefined}
                onKeyDown={guardComposingEnter}
                onChange={(e) => a.set(e.target.value)}
              />
            </GroupInputRow>
          </GroupList>
        </GroupSection>
      </form>
      <GroupSection title={REST_MSG.wordsSectionSuggest} data-testid="word-sec-suggest">
        <GroupList plain>
          {info.suggestions[product].map((s) => (
            <GroupCheckRow
              key={s}
              title={s}
              checked={word === s}
              onChange={() => a.set(s === base ? "" : s)}
              buttonTestId="word-suggest"
              data-word={s}
            />
          ))}
        </GroupList>
      </GroupSection>
      <GroupSection title={REST_MSG.wordsSectionWhere} data-testid="word-sec-where">
        <GroupList plain>
          <GroupTextRow>{t(info.where)}</GroupTextRow>
        </GroupList>
      </GroupSection>
    </div>
  );
}

/** 1語の画面の字数の上限（#289：{ongoingProject} は 6文字、ほかは 20文字） */
const maxOf = (k: VocabKey | null): number => (k === "ongoingProject" ? ONGOING_WORD_MAX : 20);
